import { useEffect } from "react";

// Default font settings
const DEFAULT_FONTS = {
  navbarFont: "Montserrat",
  heroTitleFont: "Montserrat",
  headingsFont: "Montserrat",
  bodyFont: "Sohne",
};

const hasExplicitFontClass = (el) =>
  [...el.classList].some((className) => className.startsWith("font-"));
const hasExplicitFontContext = (el) => {
  let current = el;
  while (current) {
    if (current instanceof HTMLElement && hasExplicitFontClass(current)) {
      return true;
    }
    current = current.parentElement;
  }
  return false;
};

export const useGlobalFonts = () => {
  useEffect(() => {
    // Get font settings from localStorage or use defaults
    const navbarFont =
      localStorage.getItem("navbarFont") || DEFAULT_FONTS.navbarFont;
    const heroTitleFont =
      localStorage.getItem("heroTitleFont") || DEFAULT_FONTS.heroTitleFont;
    const headingsFont =
      localStorage.getItem("headingsFont") || DEFAULT_FONTS.headingsFont;
    const bodyFont = localStorage.getItem("bodyFont") || DEFAULT_FONTS.bodyFont;

    // Apply navbar font - target nav and its direct children
    const navbar = document.querySelector("nav");
    if (navbar instanceof HTMLElement) {
      navbar.style.setProperty(
        "font-family",
        `${navbarFont}, sans-serif`,
        "important",
      );
      // Also apply to links and buttons in navbar
      const navbarChildren = navbar.querySelectorAll("a, button, span, div");
      navbarChildren.forEach((el) => {
        if (el instanceof HTMLElement) {
          el.style.setProperty(
            "font-family",
            `${navbarFont}, sans-serif`,
            "important",
          );
        }
      });
    }

    // Apply hero title font
    const heroTitle = document.querySelector("#home h1");
    if (heroTitle instanceof HTMLElement) {
      heroTitle.style.setProperty(
        "font-family",
        `${heroTitleFont}, sans-serif`,
        "important",
      );
    }

    // Apply hero subtitle font (paragraph in #home section)
    const heroSubtitle = document.querySelector("#home p");
    if (heroSubtitle instanceof HTMLElement) {
      heroSubtitle.style.setProperty(
        "font-family",
        `${heroTitleFont}, sans-serif`,
        "important",
      );
    }

    // Apply headings font (h2, h3) - but exclude hero title and navbar
    const headings = document.querySelectorAll("h2, h3");
    headings.forEach((el) => {
      if (el instanceof HTMLElement) {
        if (hasExplicitFontContext(el)) {
          el.style.removeProperty("font-family");
          return;
        }
        // Skip if it's in navbar or hero section
        if (!el.closest("nav") && !el.closest("#home")) {
          el.style.setProperty(
            "font-family",
            `${headingsFont}, sans-serif`,
            "important",
          );
        }
      }
    });

    // Apply headings font to buttons (but not in navbar)
    const buttons = document.querySelectorAll("button, .btn, [role='button']");
    buttons.forEach((el) => {
      if (el instanceof HTMLElement) {
        if (hasExplicitFontContext(el)) {
          el.style.removeProperty("font-family");
          return;
        }
        // Skip if it's in navbar
        if (!el.closest("nav")) {
          el.style.setProperty(
            "font-family",
            `${headingsFont}, sans-serif`,
            "important",
          );
        }
      }
    });

    // Apply body font to body element
    const body = document.body;
    if (body) {
      body.style.setProperty(
        "font-family",
        `${bodyFont}, sans-serif`,
        "important",
      );
    }

    // Apply body font to paragraphs and other text elements, but skip navbar, hero, and headings
    const bodyTextElements = document.querySelectorAll(
      "p, span, div, li, td, th, label, input, textarea, select",
    );
    bodyTextElements.forEach((el) => {
      if (el instanceof HTMLElement) {
        // Skip if it's in navbar, hero section, or is a heading
        const isInNavbar = el.closest("nav") !== null;
        const isInHero = el.closest("#home") !== null;
        const isHeading = el.matches("h1, h2, h3, h4, h5, h6");
        const isInExplicitFontContext = hasExplicitFontContext(el);

        if (
          !isInNavbar &&
          !isInHero &&
          !isHeading &&
          !isInExplicitFontContext
        ) {
          el.style.setProperty(
            "font-family",
            `${bodyFont}, sans-serif`,
            "important",
          );
        }
      }
    });
  }, []);

  // Re-apply fonts when DOM changes (for dynamic content)
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const headingsFont =
        localStorage.getItem("headingsFont") || DEFAULT_FONTS.headingsFont;

      // Re-apply headings font
      const headings = document.querySelectorAll("h2, h3");
      headings.forEach((el) => {
        if (el instanceof HTMLElement) {
          if (hasExplicitFontContext(el)) {
            el.style.removeProperty("font-family");
            return;
          }
          if (!el.closest("nav") && !el.closest("#home")) {
            el.style.setProperty(
              "font-family",
              `${headingsFont}, sans-serif`,
              "important",
            );
          }
        }
      });

      // Re-apply button fonts
      const buttons = document.querySelectorAll(
        "button, .btn, [role='button']",
      );
      buttons.forEach((el) => {
        if (el instanceof HTMLElement) {
          if (hasExplicitFontContext(el)) {
            el.style.removeProperty("font-family");
            return;
          }
          if (!el.closest("nav")) {
            el.style.setProperty(
              "font-family",
              `${headingsFont}, sans-serif`,
              "important",
            );
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);
};
